import { Link, useLocation } from 'react-router-dom';
import { Shield, FileText } from 'lucide-react';
import { LEGAL_PAGES_HIDDEN } from '../lib/version';

/**
 * Global App Footer - Legal & Compliance
 * Renders at the bottom of all logged-in pages
 * Minimalist design to not distract from core workflows
 *
 * v3.18.27 — Auto-applies a dark theme variant on pages that use a dark
 * background (currently the Marketplace Sentinel). Keeps the footer
 * visually integrated with the page instead of slicing a bright white
 * band into a dark workspace.
 */
const DARK_ROUTE_PATTERNS = [
  /\/sentinel$/,
  /\/sentinel\//,
];

export default function AppFooter() {
  const currentYear = new Date().getFullYear();
  const { pathname } = useLocation();
  const isDark = DARK_ROUTE_PATTERNS.some((re) => re.test(pathname));

  return (
    <footer
      className={
        isDark
          ? 'w-full border-t border-slate-700/50 bg-slate-900 mt-auto'
          : 'w-full border-t border-slate-100 bg-slate-50/50 mt-auto'
      }
      data-testid="app-footer"
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          {/* Copyright */}
          <div className="flex items-center gap-1">
            <span>© {currentYear} mngcgroup. All rights reserved.</span>
          </div>

          {/* Legal Links — hidden while LEGAL_PAGES_HIDDEN flag is on */}
          {!LEGAL_PAGES_HIDDEN && (
            <div className="flex items-center gap-6">
              <Link
                to="/terms"
                className={`flex items-center gap-1.5 transition-colors ${isDark ? 'hover:text-slate-300' : 'hover:text-slate-600'}`}
                data-testid="footer-terms-link"
              >
                <FileText className="w-3 h-3" />
                <span>Terms of Use</span>
              </Link>
              <Link
                to="/privacy"
                className={`flex items-center gap-1.5 transition-colors ${isDark ? 'hover:text-slate-300' : 'hover:text-slate-600'}`}
                data-testid="footer-privacy-link"
              >
                <Shield className="w-3 h-3" />
                <span>Privacy Policy</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
